import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { createNavigationHandler } from '../../utils/navigationUtils';

import PageNavigation from './PageNavigation';
import TimelineNavigation from './TimelineNavigation';
import { useTeachModuleState } from './useTeachModuleState';

import './TeachModule.css';

// === CONSTANTS ===
const DEBUG = true;

const logDebug = (...args) => {
    if (DEBUG) console.log('📚 [TeachModule]', ...args);
};

// Pagination Limits (from user spec)
const ITEMS_PER_PAGE = {
    steps: 5,
    rules: 5,
    examples: 3,
    vocab: 6,
    mistakes: 3
};

// === PEDAGOGICAL COMPONENTS ===

const GrammarBadge = ({ title }) => (
    <div className="grammar-badge">
        <i className="fas fa-graduation-cap"></i> {title}
    </div>
);

const StageIndicator = ({ stage }) => (
    <div className={`stage-indicator stage-${stage.key}`}>
        <span className="stage-icon">
            {stage.key === 'engage' && '🎯'}
            {stage.key === 'study' && '📚'}
            {stage.key === 'activate' && '✏️'}
            {stage.key === 'consolidate' && '✅'}
        </span>
        <span className="stage-name">{stage.name}</span>
    </div>
);

const TeacherPrompt = ({ prompt, stage }) => (
    <div className={`teacher-prompt stage-${stage}`}>
        <div className="prompt-bubble">
            {prompt}
        </div>
    </div>
);

// === RENDERERS ===

const ScenarioRenderer = ({ content }) => (
    <div className="scenario-content">
        {content.title_kr && <h2 className="scenario-title-kr">{content.title_kr}</h2>}
        {content.title_en && <h3 className="scenario-title-en">{content.title_en}</h3>}
        {content.body_text && <p className="scenario-body">{content.body_text}</p>}
        {content.visual_hint && (
            <div className="visual-hint-box">
                <i className="fas fa-lightbulb"></i>
                <span>Picture: {content.visual_hint.replace(/_/g, ' ')}</span>
            </div>
        )}
    </div>
);

const DialogueRenderer = ({ content }) => {
    // Handling new flat structure: content.dialogue_box might exist, or content might comprise fields directly
    // The previous implementation handled both. Let's maintain that robustness.
    const leftContent = content.dialogue_box || content;
    const allDialogue = useMemo(() => leftContent.dialogue || [], [leftContent.dialogue]);

    // Auto-reveal logic
    const [revealedCount, setRevealedCount] = useState(0);
    const [showTranslations, setShowTranslations] = useState(false);

    useEffect(() => {
        if (revealedCount < allDialogue.length) {
            const timer = setTimeout(() => {
                setRevealedCount(prev => prev + 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (revealedCount === allDialogue.length && allDialogue.length > 0) {
            setShowTranslations(true);
        }
    }, [revealedCount, allDialogue.length]);

    // Reset when content changes
    useEffect(() => {
        setRevealedCount(0);
        setShowTranslations(false);
    }, [allDialogue]); // Check if dependency is correct. If dialogue array ref changes, this runs.

    const visibleDialogue = allDialogue.slice(0, revealedCount);

    const getSpeakerColor = (speaker) => {
        // Simple mapping or hash could work
        switch (speaker) {
            case 'Yuna': return '#e0e7ff';
            case 'Minho': return '#dcfce7';
            default: return '#f1f5f9';
        }
    };

    return (
        <div className="dialogue-layout">
            <div className="dialogue-notes-column">
                {/* Notes placeholder */}
            </div>

            <div className="dialogue-main-column">
                {leftContent.subtitle && (
                    <h2 className="dialogue-subtitle">
                        {leftContent.subtitle.replace(/^[^\w\s]+\s*/, '')}
                    </h2>
                )}

                {visibleDialogue.length > 0 && (
                    <div className="dialogue-thread">
                        {visibleDialogue.map((d, i) => {
                            // ENHANCED: Null check for dialogue item and speaker
                            if (!d || !d.speaker) return null;
                            return (
                                <div
                                    key={i}
                                    className={`dialogue-bubble ${i % 2 === 0 ? 'narrator' : 'character'} animate-bubble-in`}
                                    style={{ backgroundColor: getSpeakerColor(d.speaker) }}
                                >
                                    <div className="bubble-speaker">{d.speaker}:</div>
                                    <div className="bubble-text-kr">{d.text || ''}</div>
                                    {showTranslations && d.translation && (
                                        <div className="bubble-text-en">({d.translation})</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const ExplanationRenderer = ({ content }) => {
    // "Noticing" steps also use this renderer basically
    return (
        <div className="explanation-content">
            {content.title_kr && <h2 className="main-kr-title">{content.title_kr}</h2>}
            {content.title_en && <h4 className="sub-en-title">{content.title_en}</h4>}
            {content.title && !content.title_kr && <div className="content-subtitle">{content.title}</div>}

            {content.body_text && <p className="body-text">{content.body_text}</p>}

            {content.core_meaning && (
                <div className="teacher-note note-info">
                    <span className="note-text"><strong>Core Meaning:</strong> {content.core_meaning}</span>
                </div>
            )}

            {content.when_to_use && (
                <div className="key-rules-section">
                    <h4 className="rules-heading">When to use:</h4>
                    <ul className="rule-list">
                        {content.when_to_use.map((item, i) => <li key={i}><span className="rule-bullet">•</span>{item}</li>)}
                    </ul>
                </div>
            )}

            {content.pattern_display && (
                <div className="pattern-formula-box">
                    <div className="formula-display">{content.pattern_display.formula}</div>
                </div>
            )}

            {content.example_kr && (
                <div className="live-example-box">
                    <div className="example-korean">{content.example_kr}</div>
                    <div className="example-english">"{content.example_en}"</div>
                </div>
            )}

            {content.breakdown && (
                <div className="breakdown-grid">
                    {Object.entries(content.breakdown).map(([key, value], i) => (
                        <div key={i} className="breakdown-item">
                            <span className="breakdown-key">{key}</span>
                            <span className="breakdown-arrow">→</span>
                            <span className="breakdown-value">{value}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// === RULE RENDERER WITH INTERNAL PAGINATION ===
const RuleRenderer = ({ content }) => {
    const leftContent = content.dialogue_box || content;
    const rightContent = content.board;

    // --- LEFT COLUMN PAGINATION ---
    const [leftPage, setLeftPage] = useState(0);
    const leftSteps = leftContent?.steps || [];
    const totalLeftPages = Math.ceil(leftSteps.length / ITEMS_PER_PAGE.steps);
    const visibleLeftSteps = leftSteps.slice(leftPage * ITEMS_PER_PAGE.steps, (leftPage + 1) * ITEMS_PER_PAGE.steps);

    // --- RIGHT COLUMN PAGINATION ---
    const [rightPage, setRightPage] = useState(0);
    const rightRules = rightContent?.rules || [];
    const totalRightPages = Math.ceil(rightRules.length / ITEMS_PER_PAGE.rules);
    const visibleRightRules = rightRules.slice(rightPage * ITEMS_PER_PAGE.rules, (rightPage + 1) * ITEMS_PER_PAGE.rules);

    // Reset pages when content changes (important for navigation)
    useEffect(() => {
        setLeftPage(0);
        setRightPage(0);
    }, [content]);

    return (
        <div className="split-board-container" style={{ flexDirection: 'row' }}>
            {/* Left Column */}
            <div className="board-column story-column">
                {leftContent.subtitle && <div className="content-subtitle">{leftContent.subtitle}</div>}
                {leftContent.title && <h2 className="title-korean" style={{ fontSize: '1.5rem' }}>{leftContent.title}</h2>}
                {leftContent.body_text && <p className="body-text">{leftContent.body_text}</p>}

                {leftContent.example_kr && (
                    <div className="live-example-box">
                        <div className="example-korean">{leftContent.example_kr}</div>
                        <div className="example-english">{leftContent.example_en}</div>
                    </div>
                )}

                {visibleLeftSteps.length > 0 && (
                    <div className="construction-steps">
                        {visibleLeftSteps.map((s, i) => (
                            <div key={i} className="step-item">
                                <div className="step-number">Step {s.step}</div>
                                <div className="step-content">
                                    <div className="step-action">{s.action}</div>
                                    {s.example && <div className="step-value">{s.example}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <PageNavigation
                    currentPage={leftPage}
                    totalPages={totalLeftPages}
                    onPrev={() => setLeftPage(p => Math.max(0, p - 1))}
                    onNext={() => setLeftPage(p => Math.min(totalLeftPages - 1, p + 1))}
                />
            </div>

            {/* Right Column */}
            <div className="board-column lesson-column">
                {rightContent && (
                    <>
                        {rightContent.subtitle && <div className="content-subtitle">{rightContent.subtitle}</div>}
                        {rightContent.title && <h2 className="title-korean" style={{ fontSize: '1.5rem' }}>{rightContent.title}</h2>}
                        {rightContent.body_text && <p className="body-text">{rightContent.body_text}</p>}

                        {visibleRightRules.length > 0 && (
                            <ul className="rule-list">
                                {visibleRightRules.map((r, i) => (
                                    <li key={i}><span className="rule-bullet">•</span>{r}</li>
                                ))}
                            </ul>
                        )}

                        {rightContent.comparison_table && (
                            <table className="comparison-table" style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                        {rightContent.comparison_table.headers.map((h, i) => (
                                            <th key={i} style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 'bold' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rightContent.comparison_table.rows.map((row, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            {row.map((cell, j) => <td key={j} style={{ padding: '0.5rem' }}>{cell}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {rightContent.important_note && (
                            <div className={`teacher-note note-${rightContent.important_note.style || 'info'}`}>
                                <span className="note-text">
                                    {rightContent.important_note.icon} {rightContent.important_note.text}
                                </span>
                            </div>
                        )}

                        <PageNavigation
                            currentPage={rightPage}
                            totalPages={totalRightPages}
                            onPrev={() => setRightPage(p => Math.max(0, p - 1))}
                            onNext={() => setRightPage(p => Math.min(totalRightPages - 1, p + 1))}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const ExampleRenderer = ({ content }) => {
    // Can be split or single
    const leftContent = content.dialogue_box || content;
    const rightContent = content.board;

    // Simplification: Render logic. If board exists, it's split.
    // We can reuse the structure from RuleRenderer but for Examples

    const renderSide = (sideContent) => {
        if (!sideContent) return null;

        const steps = sideContent.steps || [];
        const examples = sideContent.examples || sideContent.breakdown_examples || [];

        // Paginate Steps if many
        // (Using hooks inside loop/conditional is bad, but here we define a sub-component or just simplify)
        // Let's assume ExampleRenderer steps are usually few (<5). Ideally we'd map pagination state.
        // However, since we are inside a Functional Component, let's keep it simple.
        // For stricter pagination compliance, we'd need state. 

        // Actually, let's just create a quick self-contained pagination view here? 
        // No, React hooks rules. We should lift state.
        // BUT, since we have only one "side" or two "sides", we can declare state for both at top level.

        return (
            <div className="example-content-block">
                {sideContent.title && <div className="content-subtitle">{sideContent.title}</div>}

                {steps.length > 0 && (
                    <div className="guided-steps">
                        {steps.map((s, i) => (
                            <div key={i} className="step-item">
                                <div className="step-action">{s.action}</div>
                                <div className="step-value">{s.example}</div>
                            </div>
                        ))}
                    </div>
                )}

                {sideContent.example_kr && (
                    <div className="live-example-box">
                        <div className="example-korean">{sideContent.example_kr}</div>
                        <div className="example-english">{sideContent.example_en}</div>
                    </div>
                )}

                {examples.length > 0 && (
                    <div className="vocab-grid" style={{ gridTemplateColumns: '1fr' }}>
                        {examples.map((ex, i) => {
                            const [kr, en] = typeof ex === 'string' ? ex.split('→') : [ex.kr, ex.en];
                            return (
                                <div key={i} className="vocab-card" style={{ padding: '0.75rem' }}>
                                    <strong>{kr}</strong>
                                    <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>{en}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    if (rightContent) {
        return (
            <div className="split-board-container" style={{ flexDirection: 'row' }}>
                <div className="board-column story-column">{renderSide(leftContent)}</div>
                <div className="board-column lesson-column">{renderSide(rightContent)}</div>
            </div>
        );
    }

    return <div className="single-board-container">{renderSide(content)}</div>;
};

const PracticeRenderer = ({ content }) => {
    // Mistakes list often
    const mistakes = content.mistakes || [];
    const [page, setPage] = useState(0);
    const visibleMistakes = mistakes.slice(page * ITEMS_PER_PAGE.mistakes, (page + 1) * ITEMS_PER_PAGE.mistakes);
    const totalPages = Math.ceil(mistakes.length / ITEMS_PER_PAGE.mistakes);

    return (
        <div className="practice-mistakes-content">
            {content.title && <div className="content-subtitle">{content.title}</div>}

            {visibleMistakes.map((m, i) => (
                <div key={i} className="mistake-card">
                    <div className="mistake-incorrect">❌ {m.incorrect}</div>
                    <div className="mistake-correct">✅ {m.correct}</div>
                    <div className="mistake-explanation">💡 {m.why}</div>
                </div>
            ))}

            {content.rules && (
                <ul className="rule-list">
                    {content.rules.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
            )}

            <PageNavigation
                currentPage={page}
                totalPages={totalPages}
                onPrev={() => setPage(p => Math.max(0, p - 1))}
                onNext={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            />
        </div>
    );
};

const SummaryRenderer = ({ content }) => (
    <div className="summary-content">
        <h2 className="main-kr-title">{content.title}</h2>
        {content.pattern_display && (
            <div className="pattern-formula-box">
                <div className="formula-display">{content.pattern_display.formula}</div>
            </div>
        )}
        {content.rules && (
            <div className="summary-points">
                {content.rules.map((r, i) => <div key={i} className="summary-point">{r}</div>)}
            </div>
        )}
    </div>
);

// === MAIN DISPATCHER ===
const MicroStepRenderer = ({ step }) => {
    if (!step) return null;
    const { type, content } = step;

    // Remove legacy prefix if still present
    if (step.teacher_prompt) {
        step.teacher_prompt = step.teacher_prompt.replace(/Step \d+:?\s*/i, '');
    }

    switch (type) {
        case 'scenario': return <ScenarioRenderer content={content} />;
        case 'dialogue': return <DialogueRenderer content={content} />;
        case 'noticing':
        case 'explanation': return <ExplanationRenderer content={content} />;
        case 'rule': return <RuleRenderer content={content} />;
        case 'example': return <ExampleRenderer content={content} />;
        case 'practice': return <PracticeRenderer content={content} />;
        case 'summary': return <SummaryRenderer content={content} />;
        default: return <div>Unsupported type: {type}</div>;
    }
};

// === MAIN COMPONENT ===
const TeachModule = ({
    level,
    chapter,
    contentId,
    isOverlay = false,
    onClose,
    navigation: propNavigation
}) => {
    const navigate = useNavigate();
    const params = useParams();

    // Resolve props/params
    const lvl = level || params.level || '1';
    const ch = chapter || params.chapter || '1';

    const navigation = propNavigation || createNavigationHandler(navigate, lvl, ch, 'teach');

    const {
        currentStep,
        totalSteps,
        currentScreen,
        currentStage,
        timelineData,
        isLoading,
        meta,
        handleNext,
        handlePrev
    } = useTeachModuleState({
        level: lvl,
        chapter: ch,
        contentId,
        isOverlay,
        onClose,
        navigation,
        moduleType: 'grammar'
    });

    useEffect(() => {
        logDebug(`Step ${currentStep + 1} - ${currentScreen?.type}`);
    }, [currentStep, currentScreen]);

    // Keyboard support
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleNext, handlePrev]);

    if (isLoading || !currentScreen) {
        return <div className="loading-screen">Loading...</div>;
    }

    // ENHANCED: Null check for currentScreen
    if (!currentScreen) {
        return <div className="loading-screen">Loading step...</div>;
    }

    // Determine Layout Class
    const isSplit = currentScreen.layout === 'split_board';

    return (
        <div className={`teach-module-container ${isOverlay ? 'overlay-mode' : ''}`}>
            <div className="background-layer" />

            {/* Top Nav */}
            <div className="teach-top-nav">
                <div className="nav-left">
                    <button className="nav-btn" onClick={() => isOverlay && onClose ? onClose() : navigation.previous()}>
                        ← Back
                    </button>
                </div>
                <div className="nav-center">
                    <span className="nav-info">{meta.titleKr || meta.title}</span>
                </div>
                <div className="nav-right">
                    <button className="nav-btn" onClick={() => navigation.menu()}>Menu</button>
                </div>
            </div>

            {/* Board Layer */}
            <div className="board-layer">
                <GrammarBadge title={meta.titleKr || "Legacy"} />
                <StageIndicator stage={currentStage} />

                {/* 
                   We wrapped content in containerClass inside renderers for specific layouts, 
                   but to follow the "unified" CSS structure more strictly, 
                   the `lesson-board` class handles scrolling and max-height.
                   So we wrap the renderer output in `lesson-board`.
                 */}
                <div className={`lesson-board ${isSplit ? 'split-layout' : ''}`}>
                    {currentScreen.teacher_prompt && (
                        <TeacherPrompt prompt={currentScreen.teacher_prompt} stage={currentStage.key} />
                    )}

                    <MicroStepRenderer step={currentScreen} />
                </div>
            </div>

            {/* Timeline Layer */}
            <TimelineNavigation
                timelineData={timelineData}
                onPrev={handlePrev}
                onNext={handleNext}
                currentStepIndex={currentStep}
            />

            {/* Progress Bar */}
            <div className="progress-container">
                <div className="progress-fill" style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}></div>
            </div>
        </div>
    );
};

export default TeachModule;
